import { supabase } from '@/products/shared/services/supabase/client';
import type { Factor } from '@supabase/supabase-js';

export const MFA_OTP_LENGTH = 6;

export type MfaAssuranceStatus = {
  currentLevel: string | null;
  nextLevel: string | null;
  needsChallenge: boolean;
};

export type TotpMfaFactor = {
  id: string;
  friendlyName: string | null;
  status: Factor['status'];
};

export type TotpEnrollment = {
  factorId: string;
  qrCode: string | null;
  secret: string | null;
  uri: string | null;
};

type MfaError = { message: string; name?: string; status?: number };

type RawMfaFactor = {
  id: string;
  friendly_name?: string | null;
  status: Factor['status'];
  factor_type: string;
};

type RawListFactorsData = {
  all?: RawMfaFactor[];
  totp?: RawMfaFactor[];
  phone?: RawMfaFactor[];
};

type ListedFactor = {
  id: string;
  status: Factor['status'];
  factorType: 'totp' | 'phone' | 'other';
};

const mapFactorType = (factorType: string): ListedFactor['factorType'] => {
  if (factorType === 'totp') return 'totp';
  if (factorType === 'phone') return 'phone';
  return 'other';
};

/** Supabase puts unverified factors only on `data.all`; verified also appear on typed arrays. */
const collectTotpFactorsFromListResponse = (data: RawListFactorsData): TotpMfaFactor[] => {
  const byId = new Map<string, TotpMfaFactor>();

  const addTotp = (factor: RawMfaFactor) => {
    const factorType = factor.factor_type ?? 'totp';
    if (factorType !== 'totp') return;
    byId.set(factor.id, {
      id: factor.id,
      friendlyName: factor.friendly_name ?? null,
      status: factor.status,
    });
  };

  for (const factor of data.all ?? []) {
    addTotp(factor);
  }
  for (const factor of data.totp ?? []) {
    addTotp({ ...factor, factor_type: factor.factor_type ?? 'totp' });
  }

  return Array.from(byId.values());
};

const collectAllFactorsFromListResponse = (data: RawListFactorsData): ListedFactor[] => {
  const byId = new Map<string, ListedFactor>();

  const add = (factor: RawMfaFactor) => {
    byId.set(factor.id, {
      id: factor.id,
      status: factor.status,
      factorType: mapFactorType(factor.factor_type ?? 'totp'),
    });
  };

  for (const factor of data.all ?? []) {
    add(factor);
  }
  for (const factor of data.totp ?? []) {
    add({ ...factor, factor_type: factor.factor_type ?? 'totp' });
  }
  for (const factor of data.phone ?? []) {
    add({ ...factor, factor_type: factor.factor_type ?? 'phone' });
  }

  return Array.from(byId.values());
};

const validationError = (message: string): MfaError => ({
  message,
  name: 'ValidationError',
  status: 400,
});

export const getMfaAssuranceStatus = async (): Promise<{
  data: MfaAssuranceStatus | null;
  error: MfaError | null;
}> => {
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

  if (error) {
    return { data: null, error: { message: error.message } };
  }

  const currentLevel = data.currentLevel ?? null;
  const nextLevel = data.nextLevel ?? null;
  const needsChallenge = nextLevel === 'aal2' && currentLevel !== 'aal2';

  return {
    data: { currentLevel, nextLevel, needsChallenge },
    error: null,
  };
};

export const listVerifiedTotpFactors = async (): Promise<{
  data: TotpMfaFactor[];
  error: MfaError | null;
}> => {
  const { data, error } = await supabase.auth.mfa.listFactors();

  if (error) {
    return { data: [], error: { message: error.message } };
  }

  const factors: TotpMfaFactor[] = (data.totp ?? [])
    .filter((factor) => factor.status === 'verified')
    .map((factor) => ({
      id: factor.id,
      friendlyName: factor.friendly_name ?? null,
      status: factor.status,
    }));

  return { data: factors, error: null };
};

/** Include unverified TOTP factors (mid-enrollment) so we can clean them up before re-enrolling. */
export const listTotpFactors = async (): Promise<{
  data: TotpMfaFactor[];
  error: MfaError | null;
}> => {
  const { data, error } = await supabase.auth.mfa.listFactors();

  if (error) {
    return { data: [], error: { message: error.message } };
  }

  const factors = collectTotpFactorsFromListResponse(data as RawListFactorsData);

  return { data: factors, error: null };
};

export const enrollTotpFactor = async (
  friendlyName = 'Authenticator app',
): Promise<{ data: TotpEnrollment | null; error: MfaError | null }> => {
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName,
  });

  if (error) {
    return { data: null, error: { message: error.message } };
  }

  return {
    data: {
      factorId: data.id,
      qrCode: data.totp?.qr_code ?? null,
      secret: data.totp?.secret ?? null,
      uri: data.totp?.uri ?? null,
    },
    error: null,
  };
};

export const challengeMfaFactor = async (
  factorId: string,
): Promise<{ data: { challengeId: string } | null; error: MfaError | null }> => {
  if (!factorId.trim()) {
    return { data: null, error: validationError('Factor ID is required') };
  }

  const { data, error } = await supabase.auth.mfa.challenge({ factorId });

  if (error) {
    return { data: null, error: { message: error.message } };
  }

  return { data: { challengeId: data.id }, error: null };
};

export const verifyMfaFactor = async (
  factorId: string,
  challengeId: string,
  code: string,
): Promise<{ data: { verified: boolean } | null; error: MfaError | null }> => {
  const trimmedCode = code.trim();

  if (!factorId.trim() || !challengeId.trim()) {
    return { data: null, error: validationError('Challenge context is missing. Start again.') };
  }

  if (trimmedCode.length !== MFA_OTP_LENGTH) {
    return {
      data: null,
      error: validationError(`Verification code must be ${MFA_OTP_LENGTH} digits`),
    };
  }

  const { data, error } = await supabase.auth.mfa.verify({
    factorId,
    challengeId,
    code: trimmedCode,
  });

  if (error) {
    return { data: null, error: { message: error.message } };
  }

  // Ensure the client session reflects aal2 before gates re-check. verify()
  // returns the promoted tokens directly; refresh only if they are missing.
  if (data?.access_token && data.refresh_token) {
    await supabase.auth.setSession({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    });
  } else {
    await supabase.auth.refreshSession();
  }

  return { data: { verified: true }, error: null };
};

export const unenrollMfaFactor = async (
  factorId: string,
): Promise<{ data: { unenrolled: boolean } | null; error: MfaError | null }> => {
  if (!factorId.trim()) {
    return { data: null, error: validationError('Factor ID is required') };
  }

  const { error } = await supabase.auth.mfa.unenroll({ factorId });

  if (error) {
    return { data: null, error: { message: error.message } };
  }

  return { data: { unenrolled: true }, error: null };
};

/**
 * Start a login challenge against an already verified authenticator app.
 * Enrollment is never implicit: a user with no verified factor must scan a QR first.
 */
export const startTotpLoginChallenge = async (): Promise<{
  data: { factorId: string; challengeId: string } | null;
  error: MfaError | null;
}> => {
  const { data: factors, error: listError } = await listVerifiedTotpFactors();

  if (listError) {
    return { data: null, error: listError };
  }

  const totpFactor = factors[0];
  if (!totpFactor) {
    return { data: null, error: validationError('No verified authenticator app found') };
  }

  const { data: challenge, error: challengeError } = await challengeMfaFactor(totpFactor.id);

  if (challengeError || !challenge) {
    return {
      data: null,
      error: challengeError ?? validationError('Failed to start verification'),
    };
  }

  return {
    data: { factorId: totpFactor.id, challengeId: challenge.challengeId },
    error: null,
  };
};

const LEFTOVER_FACTOR_MESSAGE =
  'Unable to set up authenticator MFA for this account. Please reach out to your admin for help.';

const isAal2EnrollError = (message: string): boolean =>
  /aal2 required to enroll/i.test(message);

const isDuplicateFriendlyNameError = (message: string): boolean =>
  /friendly name.*already exists/i.test(message);

const enrollmentDraftStorageKey = (userId: string) => `mfa-enrollment-draft:${userId}`;

type EnrollmentDraft = {
  factorId: string;
  qrCode: string | null;
  secret: string | null;
  uri: string | null;
};

export const persistEnrollmentDraft = (userId: string, draft: EnrollmentDraft): void => {
  if (!userId.trim()) return;
  try {
    sessionStorage.setItem(enrollmentDraftStorageKey(userId), JSON.stringify(draft));
  } catch {
    // sessionStorage may be unavailable in some embedded contexts
  }
};

export const loadEnrollmentDraft = (userId: string): EnrollmentDraft | null => {
  if (!userId.trim()) return null;
  try {
    const raw = sessionStorage.getItem(enrollmentDraftStorageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as EnrollmentDraft;
    if (!parsed?.factorId) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const clearEnrollmentDraft = (userId: string): void => {
  if (!userId.trim()) return;
  try {
    sessionStorage.removeItem(enrollmentDraftStorageKey(userId));
  } catch {
    // ignore
  }
};

/**
 * Resume an in-progress TOTP enrollment after refresh. The user may already
 * have scanned the QR into their authenticator app — we re-issue a challenge
 * on the existing unverified factor instead of creating a duplicate enroll.
 */
export const resumeUnverifiedTotpEnrollment = async (
  userId?: string,
): Promise<{
  data: (TotpEnrollment & { challengeId: string }) | null;
  error: MfaError | null;
}> => {
  const { data: factors, error: listError } = await listTotpFactors();
  if (listError) {
    return { data: null, error: listError };
  }

  const unverified = factors.find((factor) => factor.status === 'unverified');
  if (!unverified) {
    return { data: null, error: null };
  }

  const { data: challenge, error: challengeError } = await challengeMfaFactor(unverified.id);
  if (challengeError || !challenge) {
    return {
      data: null,
      error: challengeError ?? validationError('Failed to start verification'),
    };
  }

  const draft = userId ? loadEnrollmentDraft(userId) : null;
  const draftMatches = draft?.factorId === unverified.id;

  return {
    data: {
      factorId: unverified.id,
      qrCode: draftMatches ? draft.qrCode : null,
      secret: draftMatches ? draft.secret : null,
      uri: draftMatches ? draft.uri : null,
      challengeId: challenge.challengeId,
    },
    error: null,
  };
};

/** All MFA factors for the current user (TOTP + phone leftovers from the SMS path). */
export const listAllMfaFactors = async (): Promise<{
  data: ListedFactor[];
  error: MfaError | null;
}> => {
  const { data, error } = await supabase.auth.mfa.listFactors();

  if (error) {
    return { data: [], error: { message: error.message } };
  }

  return { data: collectAllFactorsFromListResponse(data as RawListFactorsData), error: null };
};

/**
 * Clear factors that would block a fresh TOTP enroll:
 * - unverified factors of any type (safe at AAL1)
 * - leftover verified phone factors from the old SMS path (best effort; Auth may
 *   require AAL2, in which case the caller surfaces a migration message)
 */
export const clearFactorsBlockingTotpEnrollment = async (): Promise<{
  data: { remainingVerifiedPhoneIds: string[] };
  error: MfaError | null;
}> => {
  const { data: factors, error: listError } = await listAllMfaFactors();
  if (listError) {
    return { data: { remainingVerifiedPhoneIds: [] }, error: listError };
  }

  const removable = factors.filter(
    (factor) => factor.status === 'unverified' || factor.factorType === 'phone',
  );

  for (const factor of removable) {
    const { error: unenrollError } = await unenrollMfaFactor(factor.id);
    if (unenrollError && factor.status === 'unverified') {
      return { data: { remainingVerifiedPhoneIds: [] }, error: unenrollError };
    }
    // Verified phone may refuse at AAL1 — continue and report leftovers below.
  }

  const { data: remaining, error: refreshError } = await listAllMfaFactors();
  if (refreshError) {
    return { data: { remainingVerifiedPhoneIds: [] }, error: refreshError };
  }

  const remainingVerifiedPhoneIds = remaining
    .filter((factor) => factor.factorType === 'phone' && factor.status === 'verified')
    .map((factor) => factor.id);

  return { data: { remainingVerifiedPhoneIds }, error: null };
};

const saveEnrollmentDraftForCurrentUser = async (
  enrolled: TotpEnrollment,
): Promise<void> => {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id;
  if (!userId) return;
  persistEnrollmentDraft(userId, {
    factorId: enrolled.factorId,
    qrCode: enrolled.qrCode,
    secret: enrolled.secret,
    uri: enrolled.uri,
  });
};

export const clearEnrollmentDraftForCurrentUser = async (): Promise<void> => {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id;
  if (!userId) return;
  clearEnrollmentDraft(userId);
};

/**
 * Begin enrollment for a user with no verified TOTP factor. Stale unverified
 * factors and leftover SMS phone factors are cleared first when Auth allows it.
 */
export const startTotpEnrollment = async (): Promise<{
  data: (TotpEnrollment & { challengeId: string }) | null;
  error: MfaError | null;
}> => {
  const { data: cleared, error: clearError } = await clearFactorsBlockingTotpEnrollment();
  if (clearError) {
    return { data: null, error: clearError };
  }

  if (cleared.remainingVerifiedPhoneIds.length > 0) {
    return { data: null, error: validationError(LEFTOVER_FACTOR_MESSAGE) };
  }

  const { data: enrolled, error: enrollError } = await enrollTotpFactor();
  if (enrollError || !enrolled) {
    if (enrollError && isAal2EnrollError(enrollError.message)) {
      return { data: null, error: validationError(LEFTOVER_FACTOR_MESSAGE) };
    }
    if (enrollError && isDuplicateFriendlyNameError(enrollError.message)) {
      const { error: retryClearError } = await clearFactorsBlockingTotpEnrollment();
      if (retryClearError) {
        return { data: null, error: retryClearError };
      }
      const retry = await enrollTotpFactor();
      if (retry.error || !retry.data) {
        return { data: null, error: retry.error ?? validationError('Failed to start enrollment') };
      }
      const { data: retryChallenge, error: retryChallengeError } = await challengeMfaFactor(
        retry.data.factorId,
      );
      if (retryChallengeError || !retryChallenge) {
        return {
          data: null,
          error: retryChallengeError ?? validationError('Failed to start verification'),
        };
      }
      await saveEnrollmentDraftForCurrentUser(retry.data);
      return {
        data: { ...retry.data, challengeId: retryChallenge.challengeId },
        error: null,
      };
    }
    return { data: null, error: enrollError ?? validationError('Failed to start enrollment') };
  }

  const { data: challenge, error: challengeError } = await challengeMfaFactor(enrolled.factorId);
  if (challengeError || !challenge) {
    return {
      data: null,
      error: challengeError ?? validationError('Failed to start verification'),
    };
  }

  const enrollmentResult = { data: { ...enrolled, challengeId: challenge.challengeId }, error: null };
  await saveEnrollmentDraftForCurrentUser(enrolled);
  return enrollmentResult;
};

/**
 * Login MFA entry point: challenge a verified authenticator app, or start
 * enrollment when the user has none yet.
 */
export const startTotpLoginOrEnrollChallenge = async (): Promise<{
  data: {
    factorId: string;
    challengeId: string;
    qrCode: string | null;
    secret: string | null;
    uri: string | null;
    mode: 'challenge' | 'enroll';
  } | null;
  error: MfaError | null;
}> => {
  const { data: verified, error: listError } = await listVerifiedTotpFactors();
  if (listError) {
    return { data: null, error: listError };
  }

  if (verified.length > 0) {
    const { data: challenge, error: challengeError } = await challengeMfaFactor(verified[0].id);
    if (challengeError || !challenge) {
      return {
        data: null,
        error: challengeError ?? validationError('Failed to start verification'),
      };
    }
    return {
      data: {
        factorId: verified[0].id,
        challengeId: challenge.challengeId,
        qrCode: null,
        secret: null,
        uri: null,
        mode: 'challenge',
      },
      error: null,
    };
  }

  const { data: userData } = await supabase.auth.getUser();
  const resumed = await resumeUnverifiedTotpEnrollment(userData.user?.id);
  if (resumed.data) {
    return {
      data: {
        factorId: resumed.data.factorId,
        challengeId: resumed.data.challengeId,
        qrCode: resumed.data.qrCode,
        secret: resumed.data.secret,
        uri: resumed.data.uri,
        mode: 'enroll',
      },
      error: null,
    };
  }
  if (resumed.error) {
    return { data: null, error: resumed.error };
  }

  const { data: enrollment, error: enrollError } = await startTotpEnrollment();
  if (enrollError || !enrollment) {
    return { data: null, error: enrollError ?? validationError('Failed to start enrollment') };
  }

  return {
    data: {
      factorId: enrollment.factorId,
      challengeId: enrollment.challengeId,
      qrCode: enrollment.qrCode,
      secret: enrollment.secret,
      uri: enrollment.uri,
      mode: 'enroll',
    },
    error: null,
  };
};
